import { apiUrl } from "../utils/api";

/**
 * „Mail besprechen" (SP7): Notizen mit @-Erwähnungen an einer Graph-Mail —
 * FE-Gegenstück zu den Termin-Notizen ({@link calendarTerminService}). Die Mail
 * wird server-seitig über (mailbox, messageId) adressiert; der Snapshot
 * (subject/sender) wird beim Anlegen mitgegeben, damit die Mention-Inbox die
 * Mail ohne Live-Graph-Roundtrip anzeigen kann.
 */

/** Deckt {@link MentionNote} ab (id/text/author/timestamp/mentions). */
export interface MailNoteDto {
    id: number;
    text: string;
    author: string;
    timestamp: string;
    mentions?: string[];
}

/** Snapshot der Mail, beim Anlegen einer Notiz persistiert. */
export interface MailNoteSnapshot {
    conversationId?: string | null;
    subject?: string | null;
    senderName?: string | null;
    senderEmail?: string | null;
    receivedAt?: string | null;
}

/**
 * Baut den Mail-Snapshot (conversationId/subject/sender/receivedAt), der beim
 * Anlegen einer Notiz mitgegeben wird, aus einem Graph-`Email`. GETEILTER
 * Snapshot-Pfad für den Lesepanel-Sidebar-Reiter „Notizen" (C2) und das
 * „Besprechen"-Panel im Mail-Detail-Fenster (SP7) — so bleibt genau EIN
 * Snapshot-Aufbau, ohne driftende Felder. Strukturell getippt (Teilmenge von
 * `Email`), damit der Helfer ohne Graph-Service-Import testbar bleibt.
 */
export function buildMailNoteSnapshot(email: {
    conversationId?: string | null;
    subject?: string | null;
    senderName?: string | null;
    senderEmail?: string | null;
    date?: Date | null;
}): MailNoteSnapshot {
    const receivedAt =
        email.date instanceof Date && !Number.isNaN(email.date.getTime())
            ? email.date.toISOString()
            : null;
    return {
        conversationId: email.conversationId ?? null,
        subject: email.subject ?? null,
        senderName: email.senderName ?? null,
        senderEmail: email.senderEmail ?? null,
        receivedAt,
    };
}

export interface MailMentionItemDto {
    noteId: number;
    mailbox: string;
    messageId: string;
    conversationId: string | null;
    subject: string | null;
    senderName: string | null;
    senderEmail: string | null;
    author: string;
    text: string;
    timestamp: string;
    read: boolean;
}

async function req<T>(url: string, options?: RequestInit): Promise<T> {
    const res = await fetch(url, {
        headers: { "Content-Type": "application/json" },
        ...options,
    });
    if (!res.ok) {
        const txt = await res.text();
        throw new Error(`API error ${res.status}: ${txt}`);
    }
    if (res.status === 204) return undefined as T;
    return res.json();
}

const notesPath = (mailbox: string, messageId: string): string =>
    apiUrl(`mail/messages/${encodeURIComponent(messageId)}/notes`, { mailbox });

export const mailNoteService = {
    async listNotes(mailbox: string, messageId: string): Promise<MailNoteDto[]> {
        return req<MailNoteDto[]>(notesPath(mailbox, messageId));
    },

    async createNote(
        mailbox: string,
        messageId: string,
        input: { text: string; mentions?: string[] } & MailNoteSnapshot,
    ): Promise<MailNoteDto> {
        return req<MailNoteDto>(notesPath(mailbox, messageId), {
            method: "POST",
            body: JSON.stringify(input),
        });
    },

    async updateNote(
        noteId: number,
        input: { text: string; mentions?: string[] },
    ): Promise<MailNoteDto> {
        return req<MailNoteDto>(apiUrl(`mail/notes/${noteId}`), {
            method: "PUT",
            body: JSON.stringify(input),
        });
    },

    async removeNote(noteId: number): Promise<void> {
        await req<void>(apiUrl(`mail/notes/${noteId}`), { method: "DELETE" });
    },

    async listMyMentions(): Promise<MailMentionItemDto[]> {
        return req<MailMentionItemDto[]>(apiUrl("mail/notes/mentions/me"));
    },

    async markMentionRead(noteId: number): Promise<void> {
        await req<{ ok: true }>(apiUrl(`mail/notes/mentions/${noteId}/read`), { method: "PATCH" });
    },
};

/**
 * „Mail besprechen" ist ein Hintergrund-Feature (SP7): der @-Button erscheint
 * nur, wenn bewusst freigeschaltet — per Env (`VITE_MAIL_DISCUSS=true`) oder
 * localStorage-Flag (`kmvs.mailDiscuss = "1"`). Standard: aus (nur zeigen,
 * nicht bewerben).
 */
export function isMailDiscussEnabled(): boolean {
    try {
        if (import.meta.env?.VITE_MAIL_DISCUSS === "true") return true;
        return typeof localStorage !== "undefined" && localStorage.getItem("kmvs.mailDiscuss") === "1";
    } catch {
        return false;
    }
}
